import React from 'react';

export const Terms: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
      
      <div className="prose prose-lg max-w-none">
        <p className="text-gray-600 mb-6">
          <strong>Effective Date:</strong> January 15, 2025
        </p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Welcome to AskiiMall ("we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the AskiiMall marketplace platform, including our website at <a href="https://askiimall.ai" className="text-blue-600 hover:underline">https://askiimall.ai</a>, services, and applications (collectively, the "Platform").
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            By accessing or using our Platform, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not access or use the Platform.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            AskiiMall is a marketplace platform that:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Connects users with AI service providers through standardized protocols (MCP)</li>
            <li>Facilitates discovery, subscription, and integration of AI services</li>
            <li>Provides tools for developers to publish and monetize AI services</li>
            <li>Manages authentication, billing, and usage tracking for AI services</li>
            <li>Offers analytics and management tools for service providers</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Eligibility</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            To use our Platform, you must:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Be at least 18 years of age</li>
            <li>Have the legal capacity to enter into binding contracts</li>
            <li>Not be prohibited from using the Platform under applicable laws</li>
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Accounts</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Account Registration</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You may register using:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Email and password authentication</li>
            <li>Google OAuth authentication</li>
            <li>Other supported OAuth providers</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Account Security</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You are responsible for:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized access</li>
            <li>Using strong passwords and enabling security features</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Account Types</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We offer different account types:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li><strong>User Accounts:</strong> For discovering and subscribing to AI services</li>
            <li><strong>Developer Accounts:</strong> For publishing and monetizing AI services</li>
            <li><strong>Admin Accounts:</strong> For platform administration and service approval</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. User Conduct</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You agree NOT to:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Violate any applicable laws, regulations, or third-party rights</li>
            <li>Use the Platform for fraudulent, abusive, or illegal activities</li>
            <li>Interfere with or disrupt the Platform's functionality or security</li>
            <li>Attempt to gain unauthorized access to systems or accounts</li>
            <li>Reverse engineer, decompile, or extract source code</li>
            <li>Scrape, crawl, or spider the Platform without permission</li>
            <li>Transmit viruses, malware, or harmful code</li>
            <li>Impersonate others or provide false information</li>
            <li>Harass, abuse, or harm other users or developers</li>
            <li>Use the Platform to compete with AskiiMall</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. AI Services and Subscriptions</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">6.1 Service Discovery</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Users can discover AI services through our marketplace. Service descriptions, features, and pricing are provided by service developers and are subject to change.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">6.2 Subscriptions and Payments</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When subscribing to AI services:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>You agree to pay the fees specified by the service developer</li>
            <li>Payments are processed through our billing provider (GetLago/Stripe)</li>
            <li>Subscriptions may be usage-based, subscription-based, or hybrid models</li>
            <li>Billing occurs according to the plan's billing cycle</li>
            <li>You are responsible for all charges incurred under your account</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">6.3 Service Availability</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We do not guarantee:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Continuous, uninterrupted, or error-free operation of AI services</li>
            <li>Accuracy, reliability, or quality of third-party AI services</li>
            <li>That services will meet your specific requirements</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">6.4 Cancellation and Refunds</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Subscription cancellation and refund policies:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>You may cancel subscriptions at any time through your account dashboard</li>
            <li>Cancellations take effect at the end of the current billing period</li>
            <li>Refunds are subject to the service developer's refund policy</li>
            <li>Usage-based charges are not refundable</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Developer Terms</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">7.1 Service Publishing</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Developers who publish AI services agree to:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Provide accurate service descriptions, features, and pricing</li>
            <li>Implement services according to MCP protocol standards</li>
            <li>Maintain service availability and performance</li>
            <li>Provide adequate documentation and support</li>
            <li>Comply with all applicable laws and regulations</li>
            <li>Not publish malicious, fraudulent, or harmful services</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">7.2 Service Approval</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            All services are subject to review and approval by AskiiMall administrators. We reserve the right to reject or remove services that violate these Terms or our quality standards.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">7.3 Revenue Sharing</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Developer revenue terms:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>AskiiMall charges a platform fee on service subscriptions</li>
            <li>Developers receive payouts according to the payout schedule</li>
            <li>Developers are responsible for applicable taxes on their earnings</li>
            <li>Payment processing fees may apply</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">7.4 Service Content</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Developers retain ownership of their service code and intellectual property, but grant AskiiMall a license to:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Host, display, and distribute their services through the Platform</li>
            <li>Use service names, logos, and descriptions in marketplace listings</li>
            <li>Provide analytics and usage data to developers</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. OAuth and Third-Party Integrations</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">8.1 OAuth Authorization</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            By using OAuth to connect services:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>You authorize AskiiMall to access your data from third-party services</li>
            <li>OAuth tokens are encrypted and securely stored</li>
            <li>You can revoke OAuth authorizations at any time</li>
            <li>We use OAuth credentials solely for providing authorized services</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">8.2 Third-Party Services</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Third-party AI services are provided by independent developers. AskiiMall is not responsible for:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>The content, functionality, or quality of third-party services</li>
            <li>Data practices or privacy policies of third-party services</li>
            <li>Disputes between users and service developers</li>
            <li>Service interruptions or terminations by developers</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Intellectual Property</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 AskiiMall IP</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            The Platform, including its design, code, features, and content (excluding third-party services), is owned by AskiiMall and protected by intellectual property laws. You may not:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Copy, modify, or create derivative works of the Platform</li>
            <li>Use AskiiMall trademarks without permission</li>
            <li>Remove or alter copyright notices or branding</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">9.2 User Content</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You retain ownership of content you upload to the Platform (profile information, service descriptions, etc.) but grant AskiiMall a license to use, display, and store such content for providing the Platform services.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">9.3 Copyright Infringement</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We respect intellectual property rights. If you believe your copyright has been infringed, contact us at <a href="mailto:support@askiimall.ai" className="text-blue-600 hover:underline">support@askiimall.ai</a> with details of the alleged infringement.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Disclaimers</h2>
          <p className="text-gray-700 leading-relaxed mb-4 uppercase font-semibold">
            THE PLATFORM AND ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            We disclaim all warranties, including:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Merchantability, fitness for a particular purpose, and non-infringement</li>
            <li>Accuracy, reliability, or completeness of Platform content</li>
            <li>Uninterrupted, secure, or error-free operation</li>
            <li>Results obtained from using the Platform or AI services</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Limitation of Liability</h2>
          <p className="text-gray-700 leading-relaxed mb-4 uppercase font-semibold">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, ASKIIMALL SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS, DATA LOSS, OR BUSINESS INTERRUPTION.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our total liability to you for all claims shall not exceed the greater of:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>$100 USD, or</li>
            <li>The amount you paid to AskiiMall in the 12 months preceding the claim</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Indemnification</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            You agree to indemnify and hold harmless AskiiMall, its affiliates, officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney fees) arising from:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Your use of the Platform</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
            <li>Your content or services published on the Platform</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Termination</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">13.1 Termination by You</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You may terminate your account at any time by contacting us or using the account deletion feature. Termination does not relieve you of payment obligations for services already used.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">13.2 Termination by Us</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may suspend or terminate your account immediately if:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>You violate these Terms</li>
            <li>Your use poses security or legal risks</li>
            <li>Your account is inactive for an extended period</li>
            <li>We discontinue the Platform or services</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">13.3 Effect of Termination</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Upon termination:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Your access to the Platform will cease</li>
            <li>We may delete your account data (subject to legal retention requirements)</li>
            <li>Provisions that should survive termination (payment obligations, disclaimers, indemnification) remain in effect</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Dispute Resolution</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">14.1 Governing Law</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            These Terms are governed by and construed in accordance with the laws of the jurisdiction where AskiiMall is registered, without regard to conflict of law principles.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">14.2 Dispute Resolution Process</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            For any disputes:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li><strong>Informal Resolution:</strong> Contact us first to resolve the dispute informally</li>
            <li><strong>Mediation:</strong> If informal resolution fails, parties agree to mediation</li>
            <li><strong>Arbitration:</strong> Disputes not resolved through mediation will be subject to binding arbitration</li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">14.3 Class Action Waiver</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You agree to resolve disputes individually and waive the right to participate in class action lawsuits or class-wide arbitration.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Changes to Terms</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may update these Terms from time to time. We will notify you of material changes by:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4">
            <li>Posting the updated Terms on the Platform</li>
            <li>Updating the "Effective Date" at the top</li>
            <li>Sending email notifications for significant changes</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            Your continued use after changes indicates acceptance. If you do not agree, you must discontinue use.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. General Provisions</h2>
          
          <h3 className="text-xl font-semibold text-gray-800 mb-3">16.1 Entire Agreement</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            These Terms, along with our Privacy Policy, constitute the entire agreement between you and AskiiMall regarding the Platform.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">16.2 Severability</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            If any provision is found invalid or unenforceable, the remaining provisions remain in effect.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">16.3 Waiver</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Our failure to enforce any provision does not constitute a waiver of that provision.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">16.4 Assignment</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            You may not assign these Terms without our consent. We may assign these Terms to affiliates or in connection with a merger or acquisition.
          </p>

          <h3 className="text-xl font-semibold text-gray-800 mb-3">16.5 Force Majeure</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We are not liable for delays or failures caused by events beyond our reasonable control (natural disasters, strikes, wars, pandemics, etc.).
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Information</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            For questions about these Terms or the Platform:
          </p>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-700 mb-2">
              <strong>Email:</strong> <a href="mailto:support@askiimall.ai" className="text-blue-600 hover:underline">support@askiimall.ai</a>
            </p>
            <p className="text-gray-700 mb-2">
              <strong>Website:</strong> <a href="https://askiimall.ai" className="text-blue-600 hover:underline">https://askiimall.ai</a>
            </p>
            <p className="text-gray-700">
              <strong>AskiiMall, Inc.</strong>
            </p>
          </div>
        </section>

        <div className="border-t border-gray-200 pt-8 mt-12">
          <p className="text-gray-600 text-sm">
            Last Updated: January 15, 2025
          </p>
          <p className="text-gray-600 text-sm mt-2">
            © 2025 AskiiMall, Inc. All rights reserved.
          </p>
          <p className="text-gray-600 text-sm mt-4">
            By using AskiiMall, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
          </p>
        </div>
      </div>
    </div>
  );
};

